import type { ComponentType } from "react";
import type { SearchEngineAgentType } from "@shaarp/common";
import {
  BrainIcon,
  BriefcaseIcon,
  BuildingIcon,
  DatabaseIcon,
  GlobeIcon,
  LandmarkIcon,
  SearchIcon,
  UsersIcon,
} from "lucide-react";
import { RiGoogleFill, RiLinkedinFill } from "react-icons/ri";
import type { AgentTypeMeta } from "./types";

/**
 * Fallback metadata used when an agent type is not present in the registry
 * (e.g. the backend returned a new agent type that the current frontend
 * bundle does not know about yet — happens during rolling deploys or when
 * new agents are added server-side without a matching UI release).
 * Without this fallback, `AGENT_TYPE_REGISTRY[unknown].labelKey` crashes
 * the whole `SearchReasoning` component with a TypeError, breaking the
 * prospecting search page.
 */
export const UNKNOWN_AGENT_META: AgentTypeMeta = {
  icon: SearchIcon,
  labelKey: "prospecting.search.reasoning.agents.orchestrator",
};

export const AGENT_TYPE_REGISTRY: Record<SearchEngineAgentType, AgentTypeMeta> =
  {
    orchestrator: {
      icon: BrainIcon,
      labelKey: "prospecting.search.reasoning.agents.orchestrator",
    },
    coresignal: {
      icon: RiLinkedinFill as ComponentType<{ className?: string }>,
      labelKey: "prospecting.search.reasoning.agents.coresignal",
    },
    coresignal_jobs: {
      icon: BriefcaseIcon,
      labelKey: "prospecting.search.reasoning.agents.coresignalJobs",
    },
    websearch: {
      icon: GlobeIcon,
      labelKey: "prospecting.search.reasoning.agents.websearch",
      getDescription: (agent) =>
        (agent.properties as { query?: string }).query,
    },
    gouv: {
      icon: LandmarkIcon,
      labelKey: "prospecting.search.reasoning.agents.gouv",
    },
    scrapio: {
      icon: RiGoogleFill as ComponentType<{ className?: string }>,
      labelKey: "prospecting.search.reasoning.agents.scrapio",
      getDescription: (agent) => {
        const props = agent.properties as { type?: string; city?: string };
        return [props.type, props.city].filter(Boolean).join(", ");
      },
    },
    database: {
      icon: DatabaseIcon,
      labelKey: "prospecting.search.reasoning.agents.database",
    },
    pappers: {
      icon: SearchIcon,
      labelKey: "prospecting.search.reasoning.agents.pappers",
    },
    apolloio: {
      icon: BuildingIcon,
      labelKey: "prospecting.search.reasoning.agents.apolloio",
      getDescription: (agent) =>
        (agent.properties as { domain?: string }).domain,
    },
    employee: {
      icon: UsersIcon,
      labelKey: "prospecting.search.reasoning.agents.employee",
      getDescription: (agent) =>
        (agent.properties as { query?: string }).query,
    },
  };
